/**
 * Layer 2: DesignToken Context
 *
 * Provides the current design token set and allows switching between different
 * visual styling schemes (colors, typography, spacing).
 *
 * PI5: Integrated with theme transition utility for smooth theme switching
 * PI6: CSS variables update reactively when token set changes
 */

import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'
import type { DesignTokenSet, DesignTokenSetConfig, ColorMode } from '@/types/design-token-set'
import { DESIGN_TOKEN_SET_CONFIGS } from '@/types/design-token-set'
import { withThemeTransition } from '@/lib/theme-transition'

interface DesignTokenContextValue {
  currentTokenSet: DesignTokenSet
  tokenConfig: DesignTokenSetConfig
  setTokenSet: (tokenSet: DesignTokenSet) => void
  availableTokenSets: DesignTokenSet[]
  // DS3: Dark mode state management
  colorMode: ColorMode
  setColorMode: (mode: ColorMode) => void
  toggleColorMode: () => void
}

const DesignTokenContext = createContext<DesignTokenContextValue | undefined>(undefined)

interface DesignTokenProviderProps {
  children: ReactNode
  initialTokenSet?: DesignTokenSet
}

export function DesignTokenProvider({
  children,
  initialTokenSet,
}: DesignTokenProviderProps) {
  // PI3: Read initial token set from HTML attribute (set by blocking script)
  const getInitialTokenSet = (): DesignTokenSet => {
    if (initialTokenSet) return initialTokenSet
    if (typeof window !== 'undefined') {
      const htmlTokenSet = document.documentElement.getAttribute('data-design-token-set')
      if (htmlTokenSet && htmlTokenSet in DESIGN_TOKEN_SET_CONFIGS) {
        return htmlTokenSet as DesignTokenSet
      }
    }
    return 'elegant'
  }

  // DS3: Read initial color mode from HTML attribute or localStorage
  const getInitialColorMode = (): ColorMode => {
    if (typeof window !== 'undefined') {
      const htmlColorMode = document.documentElement.getAttribute('data-theme')
      if (htmlColorMode === 'dark' || htmlColorMode === 'light') {
        return htmlColorMode as ColorMode
      }
      const storedMode = localStorage.getItem('colorMode')
      if (storedMode === 'dark' || storedMode === 'light') {
        return storedMode as ColorMode
      }
      // Check system preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark'
      }
    }
    return 'light'
  }

  const [currentTokenSet, setCurrentTokenSet] = useState<DesignTokenSet>(getInitialTokenSet())
  const [colorMode, setColorModeState] = useState<ColorMode>(getInitialColorMode())

  // PI8: Sync design token changes across browser tabs
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleStorageChange = (event: StorageEvent) => {
      // React to designTokenSet changes from other tabs
      if (event.key === 'designTokenSet' && event.newValue) {
        const newTokenSet = event.newValue as DesignTokenSet
        if (newTokenSet in DESIGN_TOKEN_SET_CONFIGS && newTokenSet !== currentTokenSet) {
          setCurrentTokenSet(newTokenSet)
          document.documentElement.setAttribute('data-design-token-set', newTokenSet)
        }
      }
      // DS3: React to colorMode changes from other tabs
      if (event.key === 'colorMode' && event.newValue) {
        const newColorMode = event.newValue as ColorMode
        if ((newColorMode === 'light' || newColorMode === 'dark') && newColorMode !== colorMode) {
          setColorModeState(newColorMode)
          document.documentElement.setAttribute('data-theme', newColorMode)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [currentTokenSet, colorMode])

  const setTokenSet = useCallback((tokenSet: DesignTokenSet) => {
    // PI5: Wrap token set change with smooth transition
    withThemeTransition(() => {
      setCurrentTokenSet(tokenSet)

      // Update HTML data attribute for CSS selectors
      if (typeof window !== 'undefined') {
        document.documentElement.setAttribute('data-design-token-set', tokenSet)
        localStorage.setItem('designTokenSet', tokenSet)
      }
    })
  }, [])

  // DS3: Dark mode toggle with smooth transition
  const setColorMode = useCallback((mode: ColorMode) => {
    withThemeTransition(() => {
      setColorModeState(mode)

      // Update HTML data attribute for CSS selectors
      if (typeof window !== 'undefined') {
        document.documentElement.setAttribute('data-theme', mode)
        localStorage.setItem('colorMode', mode)
      }
    })
  }, [])

  const toggleColorMode = useCallback(() => {
    setColorMode(colorMode === 'light' ? 'dark' : 'light')
  }, [colorMode, setColorMode])

  // DS4: Token naming consistency
  // CSS custom properties are now defined entirely by Style Dictionary generated CSS files.
  // The CSS files use data-attribute selectors like [data-visual="elegant"][data-theme="dark"]
  // to apply tokens automatically. We no longer need to manually set individual properties.
  //
  // This eliminates the naming drift between:
  // - JSON source: typography.fontFamily.sans
  // - Style Dictionary output: --typography-font-family-sans (CSS variable)
  // - Previous manual setting: --font-sans (REMOVED - caused inconsistency)
  //
  // The context now only manages data attributes, CSS cascade handles the rest.

  useEffect(() => {
    if (typeof window === 'undefined') return // Skip on server

    const config = DESIGN_TOKEN_SET_CONFIGS[currentTokenSet]

    // Log successful token activation (development only)
    if (import.meta.env.DEV) {
      console.log(`✅ Design tokens activated: ${config.name} (${config.id}) [${colorMode} mode]`)
      console.log(`   Selectors: [data-visual="${currentTokenSet}"][data-theme="${colorMode}"]`)
    }
  }, [currentTokenSet, colorMode])

  const value: DesignTokenContextValue = {
    currentTokenSet,
    tokenConfig: DESIGN_TOKEN_SET_CONFIGS[currentTokenSet],
    setTokenSet,
    availableTokenSets: Object.keys(DESIGN_TOKEN_SET_CONFIGS) as DesignTokenSet[],
    // DS3: Dark mode state
    colorMode,
    setColorMode,
    toggleColorMode,
  }

  return (
    <DesignTokenContext.Provider value={value}>
      {children}
    </DesignTokenContext.Provider>
  )
}

export function useDesignToken() {
  const context = useContext(DesignTokenContext)
  if (!context) {
    throw new Error('useDesignToken must be used within DesignTokenProvider')
  }
  return context
}
